import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { EmployeeMonthlyHoursRow } from '@ip/shared-types';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useEmployeesApi } from '@/services/api';
import { t } from '@/i18n';

interface MonthlyHoursGridProps {
  companyNip: string;
  employeeId: string;
  /** Optional: hint that current rate is hourly (UI shows a friendly note when not). */
  isHourly: boolean;
}

interface Row {
  month: number;
  hours: number | null;
}

export function MonthlyHoursGrid({ companyNip, employeeId, isHourly }: MonthlyHoursGridProps) {
  const { listHours, upsertHours } = useEmployeesApi();
  const [year, setYear] = useState(new Date().getUTCFullYear());
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listHours(companyNip, employeeId, year)
      .then((data: EmployeeMonthlyHoursRow[]) => {
        if (cancelled) return;
        const byMonth = new Map<number, number>(data.map((r) => [r.month, r.hours]));
        setRows(Array.from({ length: 12 }, (_, i) => ({ month: i + 1, hours: byMonth.get(i + 1) ?? null })));
      })
      .catch((err) => { if (!cancelled) toast.error(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyNip, employeeId, year, listHours]);

  const yearOptions = Array.from({ length: 7 }, (_, i) => {
    const y = new Date().getUTCFullYear() - 4 + i;
    return { value: y, label: String(y) };
  });

  const handleBlur = async (row: Row, raw: string) => {
    const parsed = raw === '' ? null : Number(raw);
    if (parsed === row.hours) return;
    try {
      if (parsed !== null && parsed >= 0) {
        await upsertHours(companyNip, employeeId, { year, month: row.month, hours: parsed });
        setRows((prev) =>
          prev.map((r) => (r.month === row.month ? { ...r, hours: parsed } : r)),
        );
      }
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {!isHourly && (
        <p className="text-muted-foreground text-sm">{t('employees.hours.hourlyOnlyHint')}</p>
      )}
      <div className="flex items-center gap-2">
        <span className="text-sm">{t('employees.hours.year')}:</span>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {yearOptions.map((opt) => (
              <SelectItem key={opt.value} value={String(opt.value)}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card overflow-x-auto rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-muted-foreground w-[200px] whitespace-nowrap">
                {t('employees.hours.month')}
              </TableHead>
              <TableHead className="text-muted-foreground whitespace-nowrap">
                {t('employees.hours.hours')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 12 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={2}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              rows.map((row) => (
                <TableRow key={row.month}>
                  <TableCell className="py-2">{t(`employees.hours.months.${row.month}`)}</TableCell>
                  <TableCell className="py-2">
                    <Input
                      key={`${row.month}-${row.hours}`}
                      type="number"
                      min={0}
                      step={1}
                      className="w-[140px]"
                      defaultValue={row.hours ?? ''}
                      onBlur={(e) => handleBlur(row, e.target.value)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
